import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import api from "@/services/api.js";
import logo from "@/assets/logo.png";
import { capture } from "@/instrument";
import { useLoading } from "@/context/LoadingContext.jsx";
import { toast } from "react-hot-toast";
import { HiExternalLink } from "react-icons/hi";

const Guidelines = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const id = searchParams.get("id");
  const laboratoryToken = searchParams.get("laboratoryToken");
  const [guidelines, setGuidelines] = useState([]);
  const { startLoading, stopLoading } = useLoading();

  useEffect(() => {
    getGuidelines();
  }, []);

  const getGuidelines = async () => {
    startLoading();
    try {
      const res = await api.post(`/admission/check-in?id=${id}&token=${token}`, {});

      if (!res.ok) throw res;

      if (res.data.status === "ARCHIVED") {
        navigate(`/confirmation?id=${id}&token=${token}`);
        return;
      }

      if (res.data.guidelines?.length === 0) {
        validateAdmission(true);
        return;
      }

      setGuidelines(res.data.guidelines);
    } catch (error) {
      if (error?.code === "UNAUTHORIZED") return navigate("/");
      capture(error);
      toast.error("Une erreur est survenue lors de la récupération des recommandations.");
    }
    stopLoading();
  };

  const validateAdmission = async (guidelinesRespected) => {
    startLoading();
    try {
      const res = await api.put(`/admission/validate?id=${id}&token=${token}`, {
        laboratoryToken,
        guidelinesRespected,
      });

      if (!res.ok) throw res;

      navigate(`/confirmation?id=${id}&token=${token}`);
    } catch (error) {
      if (error?.code === "ADMISSION_INCOMPLETE") {
        toast.error(error.message);
        navigate(`/check-in-questions?id=${id}&token=${token}&laboratoryToken=${laboratoryToken}`);
      } else {
        capture(error);
        toast.error("Une erreur est survenue lors de la validation de votre présence.");
      }
    }
    stopLoading();
  };

  return (
    <div className="container mx-auto min-w-full min-h-screen flex flex-col font-sans p-5 pb-56 md:pb-36">
      <div className="mt-5">
        <img src={logo} alt="Logo" className="mx-auto h-12" />
      </div>
      <div className="container mx-auto flex justify-center items-center font-sans flex-col grow">
        <div className="max-w-3xl flex flex-col items-center justify-center">
          <h2 className="text-xl font-semibold mb-4 text-center">Vérification des recommandations</h2>
          <div className="mb-8 text-center">
            <p className="text-gray-700 mb-4">Avant votre prélèvement, veuillez confirmer que vous avez respecté les recommandations suivantes :</p>
            <div className="border rounded-lg p-4 mb-6 bg-blue-50 text-left">
              <ul className="list-disc pl-5 space-y-2">
                {guidelines?.map((guideline, index) => (
                  <li key={index} className="text-gray-800">
                    <ReactMarkdown
                      components={{
                        a: ({ node, ...props }) => (
                          <a {...props} target="_blank" rel="noopener noreferrer" className="flex items-center text-primary-600 hover:text-primary-800 underline">
                            <HiExternalLink className="w-4 h-4" />
                            {props.children}
                          </a>
                        ),
                        p: ({ node, ...props }) => (
                          <p {...props} className="flex items-center gap-1">
                            {props.children}
                          </p>
                        ),
                      }}
                    >
                      {guideline.guideline || ""}
                    </ReactMarkdown>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/80 backdrop-blur-md border-t border-gray-200 shadow-lg px-5 py-4">
        <div className="container mx-auto max-w-3xl">
          <p className="font-semibold mb-4 text-center">Avez-vous respecté toutes ces recommandations ?</p>
          <div className="flex gap-4 justify-center flex-col md:flex-row">
            <button onClick={() => validateAdmission(true)} className="bg-primary text-white px-6 py-3 rounded-md font-medium">
              Oui, j'ai tout respecté
            </button>
            <button onClick={() => validateAdmission(false)} className="bg-white border border-red-500 text-red-500 px-6 py-3 rounded-md font-medium">
              Non, j'ai un doute
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Guidelines;
